import React, { SFC, useCallback, useEffect, useState } from "react";

import "./style.scss";

/**
 * Interface for individual toggle states
 * @interface ToggleState
 * @property {string} text - The text to display for this state
 * @property {boolean} [active] - Whether this state is currently active
 */
export interface ToggleState {
    text: string;
    active?: boolean;
}

/**
 * Props interface for the Toggle component
 * @interface ToggleProps
 * @property {ToggleState[]} states - Array of possible toggle states
 * @property {string} [className] - Optional CSS class for styling
 * @property {string[]} [visibleTo] - Optional array of user roles that can see this toggle
 * @property {() => void} [onRendered] - Optional callback when component is rendered
 * @property {() => void} [onClick] - Optional callback when toggle is clicked
 */
export interface ToggleProps {
    states: ToggleState[];
    className?: string;
    visibleTo?: string[];
    onRendered?: () => void;
    onClick?: () => void;
}

/**
 * A component that cycles through different states when clicked.
 * Each state has its own text display, and only one state can be active at a time.
 * The component cycles through states in order, wrapping back to the beginning.
 * 
 * @component
 * @example
 * ```tsx
 * <Toggle 
 *   states={[
 *     { text: "On", active: true },
 *     { text: "Off" }
 *   ]}
 *   onRendered={() => console.log('Toggle rendered')}
 * />
 * ```
 */
const Toggle: SFC<ToggleProps> = (props) => {
    const { className, states, onRendered } = props;
    const css = [
        "__toggle__",
        className ? className : null,
    ].join(" ").trim();

    // Find the active state and get its text
    const state = states.find(element => element.active === true);
    const text = (state && state.text) || "";

    /** State hook for the currently active toggle state */
    const [active, setActive] = useState(state);

    /**
     * Handles the onRendered callback when component mounts or updates
     */
    const handleRendered = () => (onRendered && onRendered());

    /**
     * Handles click events by cycling to the next state in the array
     */
    const handleClick = useCallback(() => {
        if (active) {
            // get the active index;
            const index = states.findIndex(element => element === active);
            // unset everything
            states.forEach(element => element.active = false);
            // set the next active element
            const next = states[index + 1 === states.length ? 0 : index + 1];
            next.active = true;
            setActive(next);
        }
    }, [states, active, setActive]);

    // Call handleRendered on mount/update
    useEffect(() => handleRendered());

    return <div className={css} onClick={handleClick}>{text}</div>;
};

export default Toggle;
